import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  HStack,
  Text,
  Button,
  Tabs,
  TabList,
  Tab,
  Icon,
  useDisclosure,
  useToast,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Flex,
  SimpleGrid
} from '@chakra-ui/react';
import {
  FaBell,
  FaCheckDouble,
  FaTrash,
  FaSearch,
  FaVolumeUp,
  FaVolumeMute,
  FaHeart,
  FaPrayingHands,
  FaUser,
  FaCalendarCheck,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaCheck,
  FaSync
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import api from '../../config/axios';

// Melodic chime via Web Audio API
export const playChimeSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    // First tone (D5 - 587.33Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, ctx.currentTime);
    gain1.gain.setValueAtTime(0.12, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start();
    osc1.stop(ctx.currentTime + 0.45);

    // Second tone (A5 - 880Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.12);
    gain2.gain.setValueAtTime(0.12, ctx.currentTime + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.65);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.12);
    osc2.stop(ctx.currentTime + 0.65);
  } catch (err) {
    // Ignore audio permission errors
  }
};

export const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffInSecs = Math.floor((now - date) / 1000);

  if (diffInSecs < 60) return 'Just now';
  if (diffInSecs < 3600) return `${Math.floor(diffInSecs / 60)}m ago`;
  if (diffInSecs < 86400) return `${Math.floor(diffInSecs / 3600)}h ago`;
  if (diffInSecs < 172800) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const getCategoryDetails = (category) => {
  switch (category) {
    case 'donation':
      return { icon: FaHeart, colorScheme: 'green', label: 'Donation', tabIndex: 8 };
    case 'booking':
      return { icon: FaCalendarCheck, colorScheme: 'purple', label: 'Booking', tabIndex: 5 };
    case 'signup':
    case 'login':
    case 'user':
      return { icon: FaUser, colorScheme: 'blue', label: category === 'signup' ? 'New User' : 'Login', tabIndex: 23 };
    case 'prayer':
      return { icon: FaPrayingHands, colorScheme: 'orange', label: 'Prayer', tabIndex: 18 };
    case 'volunteer':
      return { icon: FaUser, colorScheme: 'teal', label: 'Volunteer', tabIndex: 23 };
    case 'feedback':
      return { icon: FaInfoCircle, colorScheme: 'yellow', label: 'Feedback', tabIndex: 12 };
    default:
      return { icon: FaInfoCircle, colorScheme: 'red', label: 'System', tabIndex: 0 };
  }
};

// Main Notification Component & Drawer Hook
export const useAdminNotificationState = ({ forceShow = false }) => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('admin_notif_sound') !== 'false';
  });

  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    localStorage.setItem('admin_notif_sound', soundEnabled);
  }, [soundEnabled]);

  const userRole = user?.role?.toLowerCase() || '';
  const isAdmin = forceShow || ['admin', 'sub-admin', 'superadmin', 'accountant'].includes(userRole);

  const fetchNotifications = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/api/admin/activity-notifications');
      if (res.data) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.warn('Could not load admin notifications:', err?.message || err);
    }
  }, [isAdmin]);

  // Initial fetch and 30s background polling
  useEffect(() => {
    if (!isAdmin) return;
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, fetchNotifications]);

  // Real-time socket event listener
  useEffect(() => {
    if (!socket || !isAdmin) return;

    const handleNewNotification = (notif) => {
      setNotifications((prev) => [notif, ...prev.filter((n) => n.id !== notif.id)]);
      setUnreadCount((prev) => prev + 1);

      if (soundEnabledRef.current) {
        playChimeSound();
      }

      toast({
        title: notif.title,
        description: notif.message,
        status: notif.type === 'success' ? 'success' : notif.type === 'warning' ? 'warning' : 'info',
        duration: 4500,
        isClosable: true,
        position: 'top-right'
      });
    };

    socket.on('admin-notification', handleNewNotification);
    return () => {
      socket.off('admin-notification', handleNewNotification);
    };
  }, [socket, isAdmin, toast]);

  const handleMarkAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.put(`/api/admin/activity-notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setLoading(true);
      await api.put('/api/admin/activity-notifications/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
      toast({
        title: 'All notifications marked as read',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (err) {
      toast({
        title: 'Failed to mark all as read',
        status: 'error',
        duration: 2500
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all activity notifications?')) return;
    try {
      setLoading(true);
      await api.delete('/api/admin/activity-notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
      toast({
        title: 'Notifications cleared',
        status: 'info',
        duration: 2000,
        isClosable: true
      });
    } catch (err) {
      toast({
        title: 'Failed to clear notifications',
        status: 'error',
        duration: 2500
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = notifications.filter((item) => {
    if (activeCategory !== 'all') {
      if (activeCategory === 'users' && !['signup', 'login', 'user'].includes(item.category)) {
        return false;
      }
      if (activeCategory !== 'users' && item.category !== activeCategory) {
        return false;
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = item.title?.toLowerCase().includes(q);
      const matchMsg = item.message?.toLowerCase().includes(q);
      return matchTitle || matchMsg;
    }
    return true;
  });

  return {
    isAdmin,
    isOpen,
    onOpen,
    onClose,
    notifications,
    filteredNotifications,
    unreadCount,
    loading,
    activeCategory,
    setActiveCategory,
    searchQuery,
    setSearchQuery,
    soundEnabled,
    setSoundEnabled,
    fetchNotifications,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleClearAll
  };
};

// Reusable Activity Item Card
export const NotificationItemCard = ({ notif, onSelectTab, onMarkAsRead, onClose }) => {
  const navigate = useNavigate();
  const details = getCategoryDetails(notif.category);
  const isRead = notif.isRead;

  const handleClick = () => {
    onMarkAsRead(notif.id);
    if (onClose) onClose();

    if (onSelectTab && typeof onSelectTab === 'function') {
      onSelectTab(details.tabIndex);
    } else {
      navigate(`/admin?tab=${details.tabIndex}`);
    }
  };

  return (
    <Box
      p={3.5}
      borderRadius="xl"
      border="1px solid"
      borderColor={isRead ? 'gray.200' : 'red.200'}
      bg={isRead ? 'white' : 'red.50'}
      _hover={{
        bg: isRead ? 'gray.50' : 'red.100',
        boxShadow: 'sm',
        transform: 'translateY(-1px)',
        cursor: 'pointer'
      }}
      onClick={handleClick}
      transition="all 0.2s"
    >
      <HStack align="flex-start" spacing={3}>
        <Box
          p={2.5}
          borderRadius="lg"
          bg={`${details.colorScheme}.100`}
          color={`${details.colorScheme}.700`}
          mt={0.5}
        >
          <Icon as={details.icon} boxSize={4} />
        </Box>

        <VStack align="flex-start" spacing={1} flex="1">
          <HStack justify="space-between" w="100%">
            <HStack spacing={1.5}>
              <Badge colorScheme={details.colorScheme} fontSize="10px" borderRadius="md" px={1.5}>
                {details.label}
              </Badge>
              {!isRead && (
                <Badge colorScheme="red" fontSize="9px" variant="solid" borderRadius="full" px={1.5}>
                  NEW
                </Badge>
              )}
            </HStack>
            <Text fontSize="10px" color="gray.500">
              {formatTimeAgo(notif.createdAt)}
            </Text>
          </HStack>

          <Text fontSize="xs" fontWeight={isRead ? '600' : '700'} color="gray.800" lineHeight="1.3">
            {notif.title}
          </Text>

          <Text fontSize="xs" color="gray.600" lineHeight="1.4">
            {notif.message}
          </Text>

          <HStack justify="space-between" w="100%" pt={1}>
            <HStack spacing={1} color="red.600" fontSize="11px" fontWeight="600">
              <Text>View Details</Text>
              <Icon as={FaExternalLinkAlt} boxSize={2.5} />
            </HStack>

            {!isRead && (
              <Tooltip label="Mark as read">
                <IconButton
                  size="xs"
                  icon={<FaCheck />}
                  variant="ghost"
                  colorScheme="green"
                  aria-label="Mark read"
                  onClick={(e) => onMarkAsRead(notif.id, e)}
                />
              </Tooltip>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
};

// 1. Primary Admin Notification Button (With Slide-out Drawer)
const AdminNotificationButton = ({
  variant = 'prominent', // 'prominent' | 'button' | 'icon' | 'header'
  forceShow = false,
  onSelectTab = null
}) => {
  const state = useAdminNotificationState({ forceShow });

  if (!state.isAdmin) return null;

  return (
    <>
      {/* Prominent Button Variant (Recommended for Admin Dashboard header) */}
      {variant === 'prominent' && (
        <Button
          leftIcon={<Icon as={FaBell} boxSize={4} color={state.unreadCount > 0 ? 'white' : 'red.600'} />}
          colorScheme="red"
          variant={state.unreadCount > 0 ? 'solid' : 'outline'}
          onClick={state.onOpen}
          size={{ base: 'sm', md: 'md' }}
          fontWeight="700"
          borderRadius="lg"
          borderColor="red.400"
          boxShadow={state.unreadCount > 0 ? '0 0 14px rgba(229, 62, 62, 0.45)' : 'none'}
          _hover={{
            bg: state.unreadCount > 0 ? 'red.600' : 'red.50',
            transform: 'translateY(-1px)',
            boxShadow: 'md'
          }}
          transition="all 0.2s"
        >
          Notifications
          <Badge
            ml={2}
            colorScheme={state.unreadCount > 0 ? 'yellow' : 'red'}
            variant="solid"
            borderRadius="full"
            px={2}
            py={0.5}
            fontSize="xs"
            fontWeight="bold"
          >
            {state.unreadCount > 99 ? '99+' : state.unreadCount}
          </Badge>
        </Button>
      )}

      {/* Standard Button Variant */}
      {variant === 'button' && (
        <Button
          leftIcon={<FaBell />}
          colorScheme="red"
          variant="outline"
          size="sm"
          onClick={state.onOpen}
        >
          Activity Alerts
          {state.unreadCount > 0 && (
            <Badge ml={2} colorScheme="red" borderRadius="full">
              {state.unreadCount}
            </Badge>
          )}
        </Button>
      )}

      {/* Header Compact Variant */}
      {variant === 'header' && (
        <Box position="relative" display="inline-block">
          <Tooltip label="Admin Activity Notifications" hasArrow placement="bottom">
            <IconButton
              icon={<FaBell />}
              aria-label="Admin Notifications"
              onClick={state.onOpen}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{
                bg: 'rgba(255,255,255,0.2)',
                transform: 'scale(1.1)'
              }}
              transition="all 0.2s"
            />
          </Tooltip>
          {state.unreadCount > 0 && (
            <Badge
              position="absolute"
              top="-3px"
              right="-3px"
              bg="red.500"
              color="white"
              fontSize="9px"
              fontWeight="bold"
              borderRadius="full"
              px={1.5}
              py={0.2}
              boxShadow="0 0 6px rgba(229, 62, 62, 0.9)"
            >
              {state.unreadCount > 99 ? '99+' : state.unreadCount}
            </Badge>
          )}
        </Box>
      )}

      {/* Icon Button Variant */}
      {variant === 'icon' && (
        <Box position="relative" display="inline-block">
          <Tooltip label="Admin Activity Notifications" hasArrow placement="bottom">
            <IconButton
              icon={<FaBell />}
              aria-label="Admin Notifications"
              onClick={state.onOpen}
              size="md"
              variant="outline"
              colorScheme="red"
              color="red.600"
              borderColor="red.300"
              _hover={{
                bg: 'red.50',
                transform: 'scale(1.08)'
              }}
              transition="all 0.2s"
            />
          </Tooltip>
          {state.unreadCount > 0 && (
            <Badge
              position="absolute"
              top="-2px"
              right="-2px"
              bg="red.500"
              color="white"
              fontSize="10px"
              fontWeight="bold"
              borderRadius="full"
              px={1.5}
              py={0.2}
              boxShadow="0 0 8px rgba(229, 62, 62, 0.8)"
            >
              {state.unreadCount > 99 ? '99+' : state.unreadCount}
            </Badge>
          )}
        </Box>
      )}

      {/* Slide-out Side Drawer */}
      <Drawer isOpen={state.isOpen} placement="right" onClose={state.onClose} size="md">
        <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(3px)" />
        <DrawerContent bg="white">
          <DrawerCloseButton mt={2} />

          {/* Drawer Header */}
          <DrawerHeader borderBottomWidth="1px" pb={3} bg="gray.50">
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" pr={6}>
                <HStack spacing={2}>
                  <Icon as={FaBell} color="red.600" boxSize={5} />
                  <Text fontSize="md" fontWeight="bold" color="gray.800">
                    Live Activity Notifications
                  </Text>
                  {state.unreadCount > 0 && (
                    <Badge colorScheme="red" borderRadius="full" px={2}>
                      {state.unreadCount} new
                    </Badge>
                  )}
                </HStack>
              </HStack>

              {/* Action Buttons & Sound Toggle */}
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <HStack spacing={2}>
                  <Button
                    size="xs"
                    leftIcon={<FaCheckDouble />}
                    colorScheme="blue"
                    variant="ghost"
                    onClick={state.handleMarkAllAsRead}
                    isLoading={state.loading}
                    isDisabled={state.unreadCount === 0}
                  >
                    Mark All Read
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<FaTrash />}
                    colorScheme="red"
                    variant="ghost"
                    onClick={state.handleClearAll}
                    isLoading={state.loading}
                    isDisabled={state.notifications.length === 0}
                  >
                    Clear All
                  </Button>
                  <IconButton
                    size="xs"
                    icon={<FaSync />}
                    variant="ghost"
                    aria-label="Refresh"
                    onClick={state.fetchNotifications}
                  />
                </HStack>

                <HStack spacing={2}>
                  <Icon
                    as={state.soundEnabled ? FaVolumeUp : FaVolumeMute}
                    color={state.soundEnabled ? 'green.500' : 'gray.400'}
                    fontSize="xs"
                  />
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel htmlFor="notif-sound" mb="0" fontSize="xs" color="gray.600" cursor="pointer">
                      Chime
                    </FormLabel>
                    <Switch
                      id="notif-sound"
                      size="sm"
                      colorScheme="green"
                      isChecked={state.soundEnabled}
                      onChange={(e) => state.setSoundEnabled(e.target.checked)}
                    />
                  </FormControl>
                </HStack>
              </HStack>

              {/* Search Box */}
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search live activities..."
                  value={state.searchQuery}
                  onChange={(e) => state.setSearchQuery(e.target.value)}
                  borderRadius="md"
                />
              </InputGroup>

              {/* Filter Tabs */}
              <Tabs
                variant="soft-rounded"
                colorScheme="red"
                size="sm"
                index={
                  state.activeCategory === 'all'
                    ? 0
                    : state.activeCategory === 'donation'
                    ? 1
                    : state.activeCategory === 'booking'
                    ? 2
                    : state.activeCategory === 'users'
                    ? 3
                    : 4
                }
                onChange={(idx) => {
                  const mapping = ['all', 'donation', 'booking', 'users', 'prayer'];
                  state.setActiveCategory(mapping[idx]);
                }}
              >
                <TabList overflowX="auto" py={1}>
                  <Tab fontSize="xs">All ({state.notifications.length})</Tab>
                  <Tab fontSize="xs">💰 Donations</Tab>
                  <Tab fontSize="xs">🪔 Bookings</Tab>
                  <Tab fontSize="xs">👤 Users</Tab>
                  <Tab fontSize="xs">🙏 Prayers</Tab>
                </TabList>
              </Tabs>
            </VStack>
          </DrawerHeader>

          {/* Drawer Body - Notification Feed */}
          <DrawerBody p={3}>
            {state.filteredNotifications.length === 0 ? (
              <VStack spacing={4} justify="center" h="50vh" color="gray.500">
                <Icon as={FaBell} boxSize={12} color="gray.300" />
                <Text fontSize="sm" fontWeight="500">
                  {state.searchQuery ? 'No matching notifications found' : 'No notifications yet'}
                </Text>
                <Text fontSize="xs" color="gray.400" textAlign="center" maxW="300px">
                  Real-time events like devotee logins, signups, donations, bookings, and prayer requests will appear here automatically.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={2.5} align="stretch">
                {state.filteredNotifications.map((notif) => (
                  <NotificationItemCard
                    key={notif.id}
                    notif={notif}
                    onSelectTab={onSelectTab}
                    onMarkAsRead={state.handleMarkAsRead}
                    onClose={state.onClose}
                  />
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// 2. Floating Action Button (FAB) for bottom-right persistent visibility
export const AdminNotificationFAB = ({ forceShow = false, onSelectTab = null }) => {
  const state = useAdminNotificationState({ forceShow });

  if (!state.isAdmin) return null;

  return (
    <>
      <Box
        position="fixed"
        bottom={{ base: '20px', md: '28px' }}
        right={{ base: '20px', md: '28px' }}
        zIndex={1050}
      >
        <Tooltip label={`Live Activity Alerts (${state.unreadCount} new)`} hasArrow placement="left">
          <Box position="relative" display="inline-block">
            <IconButton
              icon={<FaBell />}
              aria-label="Admin Notifications"
              onClick={state.onOpen}
              colorScheme="red"
              size="lg"
              isRound
              boxShadow="0 4px 18px rgba(220, 38, 38, 0.45)"
              _hover={{
                transform: 'scale(1.1)',
                boxShadow: '0 6px 24px rgba(220, 38, 38, 0.65)'
              }}
              transition="all 0.25s"
            />
            {state.unreadCount > 0 && (
              <Badge
                position="absolute"
                top="-2px"
                right="-2px"
                bg="yellow.400"
                color="black"
                fontSize="11px"
                fontWeight="extrabold"
                borderRadius="full"
                px={2}
                py={0.5}
                boxShadow="0 0 10px rgba(0,0,0,0.3)"
                border="2px solid white"
              >
                {state.unreadCount > 99 ? '99+' : state.unreadCount}
              </Badge>
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Drawer */}
      <Drawer isOpen={state.isOpen} placement="right" onClose={state.onClose} size="md">
        <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(3px)" />
        <DrawerContent bg="white">
          <DrawerCloseButton mt={2} />
          <DrawerHeader borderBottomWidth="1px" pb={3} bg="gray.50">
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" pr={6}>
                <HStack spacing={2}>
                  <Icon as={FaBell} color="red.600" />
                  <Text fontSize="md" fontWeight="bold" color="gray.800">
                    Live Activity Notifications
                  </Text>
                  {state.unreadCount > 0 && (
                    <Badge colorScheme="red" borderRadius="full" px={2}>
                      {state.unreadCount} new
                    </Badge>
                  )}
                </HStack>
              </HStack>

              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <HStack spacing={2}>
                  <Button
                    size="xs"
                    leftIcon={<FaCheckDouble />}
                    colorScheme="blue"
                    variant="ghost"
                    onClick={state.handleMarkAllAsRead}
                    isLoading={state.loading}
                    isDisabled={state.unreadCount === 0}
                  >
                    Mark All Read
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<FaTrash />}
                    colorScheme="red"
                    variant="ghost"
                    onClick={state.handleClearAll}
                    isLoading={state.loading}
                    isDisabled={state.notifications.length === 0}
                  >
                    Clear All
                  </Button>
                </HStack>
              </HStack>
            </VStack>
          </DrawerHeader>

          <DrawerBody p={3}>
            {state.filteredNotifications.length === 0 ? (
              <VStack spacing={4} justify="center" h="50vh" color="gray.500">
                <Icon as={FaBell} boxSize={12} color="gray.300" />
                <Text fontSize="sm" fontWeight="500">No notifications yet</Text>
              </VStack>
            ) : (
              <VStack spacing={2.5} align="stretch">
                {state.filteredNotifications.map((notif) => (
                  <NotificationItemCard
                    key={notif.id}
                    notif={notif}
                    onSelectTab={onSelectTab}
                    onMarkAsRead={state.handleMarkAsRead}
                    onClose={state.onClose}
                  />
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// 3. Embedded In-Page Activity Feed Component (for Tab 17 in Admin Dashboard)
export const AdminActivityFeed = ({ onSelectTab = null, forceShow = true }) => {
  const state = useAdminNotificationState({ forceShow });

  return (
    <Card bg="white" border="1px" borderColor="gray.200" borderRadius="xl" shadow="sm">
      <CardHeader borderBottomWidth="1px" pb={4} bg="gray.50" borderTopRadius="xl">
        <VStack align="stretch" spacing={4}>
          <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3}>
            <Box>
              <HStack spacing={2}>
                <Icon as={FaBell} color="red.600" boxSize={5} />
                <Heading size="md" color="gray.800">
                  Live Activity Stream & Devotee Alerts
                </Heading>
                {state.unreadCount > 0 && (
                  <Badge colorScheme="red" borderRadius="full" px={2.5} py={0.5}>
                    {state.unreadCount} unread
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Real-time stream of devotee actions: signups, logins, donations, seva bookings, and prayers.
              </Text>
            </Box>

            <HStack spacing={2}>
              <Button
                size="sm"
                leftIcon={<FaCheckDouble />}
                colorScheme="blue"
                variant="outline"
                onClick={state.handleMarkAllAsRead}
                isLoading={state.loading}
                isDisabled={state.unreadCount === 0}
              >
                Mark All Read
              </Button>
              <Button
                size="sm"
                leftIcon={<FaTrash />}
                colorScheme="red"
                variant="outline"
                onClick={state.handleClearAll}
                isLoading={state.loading}
                isDisabled={state.notifications.length === 0}
              >
                Clear All
              </Button>
              <IconButton
                size="sm"
                icon={<FaSync />}
                aria-label="Refresh notifications"
                onClick={state.fetchNotifications}
              />
            </HStack>
          </Flex>

          <HStack justify="space-between" wrap="wrap" gap={3}>
            {/* Search Box */}
            <InputGroup size="sm" maxW={{ base: 'full', md: '350px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search live activities..."
                value={state.searchQuery}
                onChange={(e) => state.setSearchQuery(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>

            {/* Filter Tabs */}
            <Tabs
              variant="soft-rounded"
              colorScheme="red"
              size="sm"
              index={
                state.activeCategory === 'all'
                  ? 0
                  : state.activeCategory === 'donation'
                  ? 1
                  : state.activeCategory === 'booking'
                  ? 2
                  : state.activeCategory === 'users'
                  ? 3
                  : 4
              }
              onChange={(idx) => {
                const mapping = ['all', 'donation', 'booking', 'users', 'prayer'];
                state.setActiveCategory(mapping[idx]);
              }}
            >
              <TabList overflowX="auto" py={1}>
                <Tab fontSize="xs">All ({state.notifications.length})</Tab>
                <Tab fontSize="xs">💰 Donations</Tab>
                <Tab fontSize="xs">🪔 Bookings</Tab>
                <Tab fontSize="xs">👤 Users</Tab>
                <Tab fontSize="xs">🙏 Prayers</Tab>
              </TabList>
            </Tabs>
          </HStack>
        </VStack>
      </CardHeader>

      <CardBody p={4}>
        {state.filteredNotifications.length === 0 ? (
          <VStack spacing={4} justify="center" py={12} color="gray.500">
            <Icon as={FaBell} boxSize={12} color="gray.300" />
            <Text fontSize="sm" fontWeight="600">
              {state.searchQuery ? 'No matching live activities found' : 'No activity notifications yet'}
            </Text>
            <Text fontSize="xs" color="gray.400" textAlign="center" maxW="400px">
              When devotees sign up, log in, donate, or book sevas, live notifications will instantly appear here with audio chimes.
            </Text>
          </VStack>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            {state.filteredNotifications.map((notif) => (
              <NotificationItemCard
                key={notif.id}
                notif={notif}
                onSelectTab={onSelectTab}
                onMarkAsRead={state.handleMarkAsRead}
              />
            ))}
          </SimpleGrid>
        )}
      </CardBody>
    </Card>
  );
};

export default AdminNotificationButton;
